import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import Facebook from "./Facebook";
import Twitter from "./Twitter";

// Complete tutorial: https://www.gatsbyjs.org/docs/add-seo-component/

const query = graphql`
  query SEO {
    site {
      buildTime(formatString: "YYYY-MM-DD")
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        defaultBanner: banner
        siteUrl
        siteName
        siteLanguage
        socialLinks
        ogLanguage
        author
        twitter
      }
    }
    sanitySettings {
      seo {
        title
        description
        image {
          asset {
            url
          }
        }
        keywords
      }
    }
  }
`;

const SEO = ({
  title: pageTitle,
  description: pageDescription,
  image: pageImage,
  location: pageLocation,
  keywords: pageKeywords,
  schema,
  article,
  publishedDate,
  updatedDate
}) => {
  const {
    site,
    sanitySettings: { seo: sanitySeo }
  } = useStaticQuery(query);

  const {
    siteMetadata: {
      siteUrl,
      defaultTitle,
      defaultDescription,
      defaultBanner,
      siteName: defaultSiteName,
      siteLanguage,
      ogLanguage,
      author,
      twitter,
      socialLinks
    }
  } = site;

  const {
    title: sanitySiteName,
    description: sanityDescription,
    image: sanityImage,
    keywords: sanityKeywords
  } = sanitySeo;

  const siteName = sanitySiteName || defaultSiteName;
  const title = pageTitle || defaultTitle;
  const description =
    pageDescription || sanityDescription || defaultDescription;
  const ogImage =
    pageImage?.asset?.url ||
    sanityImage?.asset?.url ||
    `${siteUrl}${defaultBanner}`;
  const keywords = pageKeywords || sanityKeywords;
  const url = `${siteUrl}${pageLocation?.pathname || ``}`;

  const formattedTitle = () => {
    const isHomepage = pageLocation.pathname === `/`;
    if (isHomepage) {
      return pageTitle || siteName;
    }
    return `${title} | ${siteName}`;
  };

  const seo = {
    title: formattedTitle(),
    description: description || defaultDescription,
    image: ogImage,
    keywords,
    url,
    schema,
    publishedDate,
    updatedDate
  };

  // schema.org in JSONLD format
  // https://developers.google.com/search/docs/guides/intro-structured-data
  // You can fill out the 'author', 'creator' with more data or another type (e.g. 'Organization')

  const schemaOrgWebPage = {
    "@context": `http://schema.org`,
    "@type": `WebPage`,
    url: siteUrl,
    headline: seo.title,
    inLanguage: siteLanguage,
    mainEntityOfPage: siteUrl,
    description: seo.description,
    name: siteName,
    author: {
      "@type": `Person`,
      name: author
    },
    copyrightHolder: {
      "@type": `Person`,
      name: author
    },
    copyrightYear: `2021`,
    creator: {
      "@type": `Person`,
      name: author
    },
    publisher: {
      "@type": `Person`,
      name: author
    },
    datePublished: seo.publishedDate,
    dateModified: seo.updatedDate,
    image: {
      "@type": `ImageObject`,
      url: ogImage
    }
  };

  // Initial breadcrumb list

  const itemListElement = [
    {
      "@type": `ListItem`,
      item: {
        "@id": siteUrl,
        name: `Homepage`
      },
      position: 1
    }
  ];

  let schemaArticle = null;

  if (article) {
    schemaArticle = {
      "@context": `http://schema.org`,
      "@type": `Article`,
      url: seo.url,
      author: {
        "@type": `Person`,
        name: author
      },
      copyrightHolder: {
        "@type": `Person`,
        name: author
      },
      copyrightYear: `2021`,
      creator: {
        "@type": `Person`,
        name: author
      },
      publisher: {
        "@type": `Organization`,
        name: author,
        logo: {
          "@type": `ImageObject`,
          url: ogImage
        }
      },
      datePublished: seo.publishedDate,
      dateModified: seo.updatedDate,
      description: seo.description,
      headline: seo.title,
      inLanguage: `en`,
      name: seo.title,
      image: {
        "@type": `ImageObject`,
        url: seo.image
      },
      mainEntityOfPage: seo.url
    };
    // Push current blogpost into breadcrumb list
    itemListElement.push({
      "@type": `ListItem`,
      item: {
        "@id": seo.url,
        name: seo.title
      },
      position: 2
    });
  }

  const breadcrumb = {
    "@context": `http://schema.org`,
    "@type": `BreadcrumbList`,
    description: `Breadcrumbs list`,
    name: `Breadcrumbs`,
    itemListElement
  };

  const organizationSchema = {
    "@context": `https://schema.org`,
    "@type": `Organization`,
    name: siteName,
    url: process.env.GATSBY_SITE_URL,
    logo: `${process.env.GATSBY_SITE_URL}/schema-icon.png`,
    sameAs: socialLinks || []
  };

  const defaultSchemaToUse = article ? schemaArticle : schemaOrgWebPage;

  return (
    <>
      <title>{seo.title}</title>
      {seo.description && <meta name="description" content={seo.description} />}
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      {seo.image && <meta name="image" content={seo.image} />}
      <meta name="gatsby-starter" content="Gatsby Starter Prismic" />
      {/* Insert schema.org data conditionally (webpage/article) + everytime (organization/breadcrumbs) */}
      <script type="application/ld+json">
        {JSON.stringify(schema || defaultSchemaToUse)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      <link rel="icon" type="image/png" href="/favicon.png" />
      <Facebook
        desc={seo.description}
        image={seo.image}
        title={seo.title}
        type={article ? `article` : `website`}
        url={seo.url}
        locale={ogLanguage}
        name={siteName}
      />
      <Twitter
        title={seo.title}
        image={seo.image}
        desc={seo.description}
        username={twitter}
      />
    </>
  );
};

export default SEO;
